import { useState } from 'react';
import { Card, Alert, Button, Space, message, Modal, Dropdown } from 'antd';
import { DownloadOutlined, ExpandOutlined, CloseOutlined } from '@ant-design/icons';
import OrgChartRenderer from './renderers/OrgChartRenderer';
import FlowChartRenderer from './renderers/FlowChartRenderer';
import NetworkChartRenderer from './renderers/NetworkChartRenderer';
import MermaidRenderer from './renderers/MermaidRenderer';
import TimelineRenderer from './renderers/TimelineRenderer';
import TableRenderer from './renderers/TableRenderer';
import PerformanceDashboardRenderer from './renderers/PerformanceDashboardRenderer';
import TimeSeriesChartRenderer from './renderers/TimeSeriesChartRenderer';
import { ChartParser } from '../../utils/ChartParser';

const ChartRenderer = ({ content, type, data, config = {} }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  // If content is provided, parse it to extract chart data
  if (content && !type && !data) {
    const parsed = ChartParser.parse(content);
    if (!parsed.hasChart) {
      return null; // No chart found in content
    }
    return <ChartRenderer type={parsed.type} data={parsed.data} config={config} />;
  }
  
  const renderers = {
    'org-chart': OrgChartRenderer,
    'flowchart': FlowChartRenderer,
    'network': NetworkChartRenderer,
    'mermaid': MermaidRenderer,
    'timeline': TimelineRenderer,
    'table': TableRenderer,
    'performance-dashboard': PerformanceDashboardRenderer,
    'cascade-progress': TimeSeriesChartRenderer, // Use TimeSeriesChartRenderer for cascade-progress
    'time-series': TimeSeriesChartRenderer,
  };

  const Renderer = renderers[type];

  const handleDownload = (format = 'svg') => {
    try {
      // Find the SVG element - skip icon SVGs, get the actual chart
      let svgElement = null;
      
      // Try modal first
      const modalSvgs = document.querySelectorAll('.ant-modal svg');
      for (let svg of modalSvgs) {
        // Skip icon SVGs (they have data-icon attribute)
        if (!svg.hasAttribute('data-icon') && !svg.hasAttribute('focusable')) {
          svgElement = svg;
          break;
        }
      }
      
      // If not in modal, try card
      if (!svgElement) {
        const cardSvgs = document.querySelectorAll('.chart-card svg');
        for (let svg of cardSvgs) {
          // Skip icon SVGs
          if (!svg.hasAttribute('data-icon') && !svg.hasAttribute('focusable')) {
            svgElement = svg;
            break;
          }
        }
      }
      
      if (!svgElement) {
        console.error('No chart SVG found');
        message.error('No chart found to download');
        return;
      }
      
      console.log('Found chart SVG:', svgElement);

      if (format === 'svg') {
        // Download as SVG
        const svgClone = svgElement.cloneNode(true);
        
        // Ensure proper SVG attributes
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        
        // Get current dimensions if not set
        const bbox = svgElement.getBoundingClientRect();
        if (!svgClone.hasAttribute('width')) {
          svgClone.setAttribute('width', bbox.width);
        }
        if (!svgClone.hasAttribute('height')) {
          svgClone.setAttribute('height', bbox.height);
        }
        
        const svgData = new XMLSerializer().serializeToString(svgClone);
        console.log('SVG data length:', svgData.length);
        
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);

        const downloadLink = document.createElement('a');
        downloadLink.href = svgUrl;
        downloadLink.download = `chart-${type}-${Date.now()}.svg`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(svgUrl);

        message.success('Chart downloaded as SVG!');
      } else {
        // Download as PNG or JPG
        // Try to get dimensions from attributes first, then bounding box
        let width = parseInt(svgElement.getAttribute('width')) || 0;
        let height = parseInt(svgElement.getAttribute('height')) || 0;
        
        // If no attributes, try bounding box
        if (width === 0 || height === 0) {
          const bbox = svgElement.getBoundingClientRect();
          width = Math.ceil(bbox.width);
          height = Math.ceil(bbox.height);
        }
        
        // If still no dimensions, try viewBox
        if (width === 0 || height === 0) {
          const viewBox = svgElement.getAttribute('viewBox');
          if (viewBox) {
            const parts = viewBox.split(/\s+/);
            if (parts.length === 4) {
              width = parseInt(parts[2]);
              height = parseInt(parts[3]);
            }
          }
        }

        console.log('SVG dimensions:', width, height);

        if (width === 0 || height === 0) {
          message.error('Chart has no dimensions. Try downloading as SVG instead.');
          return;
        }

        // Clone SVG and set proper attributes
        const svgClone = svgElement.cloneNode(true);
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        svgClone.setAttribute('width', width);
        svgClone.setAttribute('height', height);
        
        // Set viewBox if not present
        if (!svgClone.hasAttribute('viewBox')) {
          svgClone.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }
        
        console.log('SVG clone created with viewBox:', svgClone.getAttribute('viewBox'));

        // Serialize SVG
        const svgString = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const scale = 3; // 3x for high quality
            canvas.width = width * scale;
            canvas.height = height * scale;
            const ctx = canvas.getContext('2d');

            // Set high quality rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Background
            if (format === 'jpg') {
              ctx.fillStyle = 'white';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            } else {
              // Transparent for PNG
              ctx.clearRect(0, 0, canvas.width, canvas.height);
            }

            // Draw scaled
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);

            // Convert to blob and download
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  message.error('Failed to create image');
                  return;
                }
                
                const downloadUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `chart-${type}-${Date.now()}.${format}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                URL.revokeObjectURL(downloadUrl);
                URL.revokeObjectURL(url);
                
                message.success(`Chart downloaded as ${format.toUpperCase()}!`);
              },
              `image/${format === 'jpg' ? 'jpeg' : 'png'}`,
              1.0
            );
          } catch (err) {
            console.error('Canvas error:', err);
            URL.revokeObjectURL(url);
            message.error('Failed to create image');
          }
        };

        img.onerror = (err) => {
          console.error('Image load error:', err);
          URL.revokeObjectURL(url);
          message.error('Failed to load SVG for conversion');
        };

        img.src = url;
      }
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download chart');
    }
  };

  const downloadMenuItems = [
    {
      key: 'svg',
      label: 'Download as SVG',
      onClick: () => handleDownload('svg'),
    },
    {
      key: 'png',
      label: 'Download as PNG',
      onClick: () => handleDownload('png'),
    },
    {
      key: 'jpg',
      label: 'Download as JPG',
      onClick: () => handleDownload('jpg'),
    },
  ];

  if (!Renderer) {
    return (
      <Alert
        message="Unsupported Chart Type"
        description={`Chart type "${type}" is not yet supported.`}
        type="warning"
        showIcon
      />
    );
  }

  return (
    <>
      <Card 
        className="chart-card"
        bodyStyle={{ padding: '20px', cursor: 'pointer' }}
        style={{ 
          marginTop: '12px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
        }}
        hoverable
        onClick={() => setIsModalVisible(true)}
        extra={
          <Space onClick={(e) => e.stopPropagation()}>
            <Button 
              icon={<ExpandOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                setIsModalVisible(true);
              }}
              size="small"
              title="View Fullscreen"
            >
              Expand
            </Button>
            <Dropdown 
              menu={{ items: downloadMenuItems }} 
              placement="bottomRight"
              trigger={['click']}
            >
              <Button 
                icon={<DownloadOutlined />} 
                onClick={(e) => e.stopPropagation()}
                size="small"
                type="primary"
                title="Download chart"
              >
                Download
              </Button>
            </Dropdown>
          </Space>
        }
      >
        <Renderer data={data} config={config} />
      </Card>

      <Modal
        title={`${type.charAt(0).toUpperCase() + type.slice(1)} - Fullscreen View`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width="95vw"
        style={{ top: 20 }}
        bodyStyle={{ 
          height: 'calc(95vh - 110px)', 
          overflow: 'auto',
          padding: '24px',
          background: '#fafafa',
        }}
        footer={[
          <Dropdown 
            key="download"
            menu={{ items: downloadMenuItems }} 
            placement="topRight"
            trigger={['click']}
          >
            <Button 
              icon={<DownloadOutlined />} 
              type="primary"
            >
              Download
            </Button>
          </Dropdown>,
          <Button 
            key="close" 
            icon={<CloseOutlined />} 
            onClick={() => setIsModalVisible(false)}
          >
            Close
          </Button>,
        ]}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '100%',
        }}>
          <Renderer 
            data={data} 
            config={{
              ...config,
              width: window.innerWidth * 0.85,
              height: window.innerHeight * 0.75,
            }} 
          />
        </div>
      </Modal>
    </>
  );
};

export default ChartRenderer;
